// One recorded call: listen from any moment the AI points at (PLAY-01..03), read the transcript, see how the call did
// on the company's checklist (PIPE-13), and confirm or correct what the AI proposed (REVIEW-01).
import { useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import {
  fmtDateTime,
  fmtDue,
  fmtDuration,
  fmtPhone,
  isAdminRole,
  TASK_TYPES,
  taskTypeLabel,
  toWallTime,
} from "../lib/format";
import type { AnalysisOutput, NoteLayer, Segment, TaskType } from "../lib/types";
import { layerEffect } from "../lib/callNotes";
import { useMember } from "../app/session";
import { Card, Empty, ErrorNote, InterestBadge, Loading, Modal, Page, useAction, useLoad } from "../app/ui";
import { RescheduleModal } from "./LeadDetail";

function Moment(
  { t, onPlay }: { t: number | null | undefined; onPlay: (t: number) => void },
) {
  if (t == null) return null;
  return (
    <button
      className="moment"
      onClick={() => onPlay(t)}
      title="Play from here"
    >
      ▶ {fmtDuration(t)}
    </button>
  );
}

const statusText: Record<string, string> = {
  queued: "Waiting to be processed.",
  processing: "Being transcribed and analysed now.",
  held: "Waiting: AI processing is paused or this month's AI minutes are used up.",
  failed: "Could not be processed.",
};

export function CallDetail() {
  const { id = "" } = useParams();
  const [params] = useSearchParams();
  const { api, member, tz } = useMember();
  const { data, error, loading, reload } = useLoad(() => api.callDetail(id), [
    api,
    id,
  ]);
  const members = useLoad(() => api.members(), [api]);
  const { busy, run } = useAction();
  const audio = useRef<HTMLAudioElement>(null);
  const [src, setSrc] = useState<string | null>(null);
  const [pendingT, setPendingT] = useState<number | null>(null);
  const [playError, setPlayError] = useState<string | null>(null);
  const [now, setNow] = useState(0);
  const [correcting, setCorrecting] = useState(false);
  const [runningAgain, setRunningAgain] = useState(false);
  const startT = params.get("t");

  const play = async (t = 0) => {
    setPlayError(null);
    const el = audio.current;
    if (src && el) {
      el.currentTime = t;
      void el.play().catch(() => {});
      return;
    }
    try {
      const res = await api.playback(id, t); // Checked and audit-logged on the server every time a link is made.
      setPendingT(t);
      setSrc(res.url);
    } catch (e) {
      setPlayError(
        e instanceof Error ? e.message : "The recording could not be played.",
      );
    }
  };

  useEffect(() => {
    if (startT && data?.recording.status === "done") void play(Number(startT));
    // Only when the page opens with ?t=.
  }, [data?.recording.id]);

  if (loading && !data) return <Loading />;
  if (!data) return <ErrorNote error={error ?? "Call not found."} />;

  const { recording, call, lead, transcript, analysis, insights, reviews } = data;
  const output = (analysis?.output ?? {}) as AnalysisOutput;
  const segments = (transcript?.segments ?? []) as unknown as Segment[];
  // A few seconds of audio, or more words than it could hold: nothing was read from it (D-159).
  const noConversation = recording.error_kind === "no_conversation" ? recording.error ?? "No conversation." : null;
  const caller = (members.data ?? []).find((m) => m.id === recording.member_id);
  const names = new Map(
    (members.data ?? []).map((m) => [m.id, m.display_name]),
  );
  const canReview = recording.member_id === member.member_id ||
    member.role !== "caller";
  // A telecaller does not overrule a manager's review of their call (REVIEW-01); the server refuses it too.
  const managerReviewed = member.role === "caller" &&
    reviews.some((r) => (members.data ?? []).some((m) => m.id === r.member_id && m.role !== "caller"));
  const when = call?.started_at ?? recording.recorded_at ??
    recording.created_at;
  const current = segments.findIndex((s, i) =>
    s.start != null && now >= s.start &&
    (segments[i + 1]?.start == null || now < (segments[i + 1].start as number))
  );
  const objections = insights.filter((i) => i.kind === "objection");
  const competitors = insights.filter((i) => i.kind === "competitor");
  const requests = insights.filter((i) => i.kind === "request");
  const checklist = output.checklist ?? [];

  // Owners and admins can have a call transcribed and analysed again with the current AI and instructions (REC-05),
  // saying what the earlier reading got wrong (D-111).
  const rerun = async (note?: { layer: NoteLayer; text: string }) => {
    if (
      await run(
        () => api.rerunRecording(recording.id, note),
        note
          ? "Queued with your note: the call is read again shortly, and the note is in Call notes"
          : "Queued: the call is transcribed and analysed again shortly",
      )
    ) {
      setRunningAgain(false);
      setSrc(null);
      await reload();
    }
  };

  const review = async (action: "confirmed" | "not_a_lead") => {
    if (!analysis) return;
    if (
      await run(
        () => api.review(analysis.id, action),
        action === "confirmed" ? "Thanks, confirmed" : "Marked as not a lead",
      )
    ) {
      await reload();
    }
  };

  return (
    <Page
      title={lead ? (lead.name || fmtPhone(lead.phone_e164)) : "Call"}
      sub={
        <span className="row">
          <span>{fmtDateTime(when, tz)}</span>
          <span className="faint">
            · {fmtDuration(recording.duration_s ?? call?.duration_s)} · {caller?.display_name ?? ""}
          </span>
          {analysis && <InterestBadge interest={analysis.interest} />}
        </span>
      }
      actions={
        <>
          {lead && <Link className="button" to={`/leads/${lead.id}`}>Open lead</Link>}
          {isAdminRole(member.role) && recording.status !== "processing" && !recording.audio_deleted_at &&
            !recording.erase_requested_at && (
            <button
              className="small danger"
              disabled={busy}
              onClick={() => setRunningAgain(true)}
            >
              Run AI again
            </button>
          )}
        </>
      }
    >
      {runningAgain && (
        <RunAgain
          busy={busy}
          onClose={() => setRunningAgain(false)}
          onRun={(note) => void rerun(note)}
        />
      )}
      <div className="split">
        <div className="stack">
          <Card
            title="Recording"
            actions={
              <button
                className="primary small"
                onClick={() => void play(0)}
              >
                ▶ Play
              </button>
            }
          >
            <ErrorNote error={playError} />
            {src
              ? (
                <audio
                  ref={audio}
                  controls
                  src={src}
                  onLoadedMetadata={(e) => {
                    if (pendingT != null) {
                      e.currentTarget.currentTime = pendingT;
                    }
                    setPendingT(null);
                    void e.currentTarget.play().catch(() => {});
                  }}
                  onTimeUpdate={(e) => setNow(e.currentTarget.currentTime)}
                  onError={() => {
                    // The five-minute link has expired: the next play asks for a new one.
                    setSrc(null);
                  }}
                />
              )
              : (
                <p className="faint">
                  Press play or any ▶ moment. Every playback is recorded in the audit log.
                </p>
              )}
          </Card>

          <Card title="Transcript">
            {noConversation ? <Empty>{noConversation}</Empty> : !transcript && (
              <Empty>
                {statusText[recording.status] ?? "No transcript."} {recording.error ? `(${recording.error})` : ""}
              </Empty>
            )}
            <div className="transcript">
              {segments.map((s, i) => (
                <button
                  key={i}
                  className={`seg${i === current ? " now" : ""}`}
                  onClick={() => void play(s.start ?? 0)}
                >
                  <span className="t">{fmtDuration(s.start)}</span>
                  <span
                    className={`who${/customer/i.test(s.speaker ?? "") ? " customer" : ""}`}
                  >
                    {s.speaker ?? "Speaker"}
                  </span>
                  <span className="text">{s.text}</span>
                </button>
              ))}
            </div>
          </Card>
        </div>

        <div className="stack">
          <Card title="What the AI heard">
            {!analysis && (
              <Empty>
                {noConversation ?? statusText[recording.status] ?? "Not analysed yet."}
              </Empty>
            )}
            {analysis && (
              <>
                <p>{output.summary ?? analysis.summary}</p>
                {output.interest && (
                  <div style={{ marginBottom: 10 }}>
                    <div className="row">
                      <strong>Interest:</strong> <InterestBadge interest={output.interest.level} />
                      <span className="faint">
                        {Math.round((output.interest.confidence ?? 0) * 100)}% sure
                      </span>
                    </div>
                    {output.interest.evidence?.map((ev, i) => (
                      <div key={i} className="row">
                        <span className="quote">“{ev.quote}”</span> <Moment t={ev.t} onPlay={play} />
                      </div>
                    ))}
                  </div>
                )}
                {output.next_action && output.next_action.type !== "none" && (
                  <div style={{ marginBottom: 10 }}>
                    <strong>Next:</strong>
                    {taskTypeLabel[output.next_action.type as TaskType] ??
                      output.next_action.type} · {output.next_action.reason}
                    {output.next_action.customer_said && (
                      <div className="quote">
                        “{output.next_action.customer_said}”
                      </div>
                    )}
                  </div>
                )}
                {objections.length > 0 && <h3 style={{ margin: "12px 0 4px" }}>Objections</h3>}
                {objections.map((o) => (
                  <div className="list-item" key={o.id}>
                    <div className="row between">
                      <strong>{o.detail || o.category}</strong>
                      <Moment t={o.t} onPlay={play} />
                    </div>
                    {o.quote && <div className="quote">“{o.quote}”</div>}
                  </div>
                ))}
                {competitors.length > 0 && (
                  <h3 style={{ margin: "12px 0 4px" }}>
                    Other options mentioned
                  </h3>
                )}
                {competitors.map((c) => (
                  <div className="row between list-item" key={c.id}>
                    <span>{c.detail}</span>
                    <Moment t={c.t} onPlay={play} />
                  </div>
                ))}
                {requests.length > 0 && <h3 style={{ margin: "12px 0 4px" }}>They asked for</h3>}
                {requests.map((r) => <div className="list-item" key={r.id}>{r.detail}</div>)}
                {checklist.length > 0 && (
                  <>
                    <h3 style={{ margin: "12px 0 4px" }}>Call checklist</h3>
                    {analysis.call_type && (
                      <p className="faint" style={{ margin: 0 }}>Checked as a {analysis.call_type} call.</p>
                    )}
                    {checklist.map((c) => (
                      <div className={`check ${c.result}`} key={c.key}>
                        <span className={`badge ${c.result === "yes" ? "ok" : c.result === "no" ? "danger" : ""}`}>
                          {c.result === "yes" ? "Done" : c.result === "no" ? "Missed" : "Didn't apply"}
                        </span>
                        <div>
                          <div>{c.label}</div>
                          {c.quote && <div className="quote">“{c.quote}”</div>}
                        </div>
                        <Moment t={c.t} onPlay={play} />
                      </div>
                    ))}
                  </>
                )}
              </>
            )}
          </Card>

          {analysis && canReview && (
            <Card title="Is this right?">
              <p className="faint">
                Your answer corrects the follow-up and teaches Voxowa what good looks like.
              </p>
              {managerReviewed ? <p>Your manager has reviewed this call.</p> : (
                <div className="row">
                  <button
                    className="primary small"
                    disabled={busy}
                    onClick={() => void review("confirmed")}
                  >
                    Yes, correct
                  </button>
                  <button
                    className="small"
                    disabled={busy}
                    onClick={() => setCorrecting(true)}
                  >
                    Change the follow-up
                  </button>
                  <button
                    className="small danger"
                    disabled={busy}
                    onClick={() => void review("not_a_lead")}
                  >
                    Not a lead
                  </button>
                </div>
              )}
              {reviews.length > 0 && (
                <div className="list" style={{ marginTop: 10 }}>
                  {reviews.slice(0, 5).map((r) => (
                    <div className="list-item faint" key={r.id}>
                      {names.get(r.member_id) ?? "Someone"}: {r.action.replace(/_/g, " ")} ·{" "}
                      {fmtDateTime(r.created_at, tz)}
                    </div>
                  ))}
                </div>
              )}
            </Card>
          )}
          {call && (
            <p className="faint">
              Number on the call log: {fmtPhone(call.phone_e164) || call.raw_number}
            </p>
          )}
        </div>
      </div>

      {correcting && analysis && (
        <RescheduleModal
          title="Change the follow-up"
          initialType={(TASK_TYPES as string[]).includes(
              output.next_action?.type ?? "",
            )
            ? (output.next_action!.type as TaskType)
            : "call"}
          initialLocal={toWallTime(new Date(Date.now() + 24 * 3600_000), tz)}
          tz={tz}
          onClose={() => setCorrecting(false)}
          onSave={async (dueAt, type) => {
            if (
              await run(
                () => api.review(analysis.id, "corrected", { type, dueAt }),
                `Follow-up moved to ${fmtDue(dueAt, tz)}`,
              )
            ) {
              setCorrecting(false);
              await reload();
            }
          }}
        />
      )}
    </Page>
  );
}

/** Run the AI on a call again, saying where the earlier reading went wrong and what (D-111). */
function RunAgain(
  props: { busy: boolean; onClose: () => void; onRun: (note?: { layer: NoteLayer; text: string }) => void },
) {
  const [layer, setLayer] = useState<NoteLayer | null>(null);
  const [text, setText] = useState("");
  const written = text.trim() !== "";
  const ready = layer ? written : !written;
  const layers: [NoteLayer, string][] = [["transcript", "Transcript"], ["analysis", "Analysis"], ["both", "Both"]];
  return (
    <Modal title="Run the AI on this call again" onClose={props.onClose}>
      <p className="faint">
        Say what it got wrong. The AI reads your note on this run, and the note goes into Call notes, the log of where
        readings go wrong.
      </p>
      <div className="field">
        <span>Where was the mistake?</span>
        <div className="row">
          {layers.map(([value, label]) => (
            <button
              key={value}
              type="button"
              className={layer === value ? "small primary" : "small"}
              aria-pressed={layer === value}
              onClick={() => setLayer(layer === value ? null : value)}
            >
              {label}
            </button>
          ))}
        </div>
        {layer && <span className="hint">{layerEffect[layer]}</span>}
      </div>
      <label className="field">
        <span>What was wrong?</span>
        <textarea
          rows={4}
          maxLength={2000}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="For example: he asked to be called after December, but the follow-up is on Monday."
        />
      </label>
      <div className="row" style={{ justifyContent: "flex-end", marginTop: 12 }}>
        {!ready && <span className="faint">{layer ? "Write what was wrong." : "Say where the mistake was."}</span>}
        <button className="small" onClick={props.onClose}>Cancel</button>
        <button
          className="small primary"
          disabled={props.busy || !ready}
          onClick={() => props.onRun(layer && written ? { layer, text: text.trim() } : undefined)}
        >
          {layer && written ? "Run again with this note" : "Run again"}
        </button>
      </div>
    </Modal>
  );
}
